import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { Close } from '@/components/Icons/Close';
import { useLessonTimes } from '@/components/LessonTimesModal/hooks/useLessonTimes';
import { Modal } from '@/components/Modal';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { ModalSize, ModalVariant } from '@/types/components';
import { RANGE_SYMBOL } from '@/utils/text';

const SHEET_CONTENT_HEIGHT = 'max-md:min-h-[192px]';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const LessonTimesModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);
  const { data, isLoading } = useLessonTimes(isOpen);

  const title = t('diary.timetable');
  const lessonTimes = data ?? [];
  const isEmpty = !isLoading && !lessonTimes.length;

  return (
    <Modal
      isOpen={isOpen}
      label={title}
      size={ModalSize.Large}
      variant={isMobile ? ModalVariant.BottomSheet : ModalVariant.Default}
      className="max-md:max-h-[85svh]! md:w-[420px]"
      contentRef={sheetRef}
      onClose={onClose}
    >
      <div
        {...swipeProps}
        className="flex shrink-0 cursor-grab touch-none flex-col active:cursor-grabbing md:hidden"
      >
        <div className="flex justify-center py-2">
          <div className="h-[3px] w-10 rounded-full bg-drag-handle" />
        </div>
        <h2 className="text-title-36 px-4 py-3 text-foreground">{title}</h2>
      </div>

      <div className="hidden shrink-0 items-center justify-between gap-3 bg-background px-5 pt-5 pb-4 md:flex">
        <h2 className="text-headline-20 truncate text-foreground">{title}</h2>
        <button
          type="button"
          aria-label={t('global.close')}
          onClick={onClose}
          className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-foreground"
        >
          <Close className="size-4" />
        </button>
      </div>

      {isLoading && (
        <div
          className={`pointer-events-none flex flex-1 items-center justify-center md:absolute md:inset-0 ${SHEET_CONTENT_HEIGHT}`}
        >
          <PageSpinner className="size-[20px] text-subtle-foreground" />
        </div>
      )}

      {isEmpty && (
        <div
          className={`pointer-events-none flex flex-col justify-center pb-[calc(24px+env(safe-area-inset-bottom))] md:absolute md:inset-0 md:pb-0 ${SHEET_CONTENT_HEIGHT}`}
        >
          <EmptyState description={t('diary.emptyTimetable')} />
        </div>
      )}

      {!!lessonTimes.length && (
        <AutoHideScrollArea
          hasTopShadow
          className={`flex min-h-0 flex-1 flex-col ${SHEET_CONTENT_HEIGHT}`}
        >
          <div className="flex flex-col pb-[calc(24px+env(safe-area-inset-bottom))] md:pt-2 md:pb-0">
            {lessonTimes.map((lessonTime, index) => (
              <div
                key={index}
                className="flex items-start gap-4 bg-background p-4 even:bg-on-primary-surface"
              >
                <p className="text-body-14 min-w-0 flex-1 text-foreground">
                  {lessonTime.name}
                </p>
                <p className="text-body-14 min-w-0 flex-1 text-right text-foreground">
                  {`${lessonTime.from} ${RANGE_SYMBOL} ${lessonTime.to}`}
                </p>
              </div>
            ))}
          </div>
        </AutoHideScrollArea>
      )}
    </Modal>
  );
};
